import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

// --- Types ---

export interface ShortcutAction {
  key: string
  description: string
  category: string
  handler: () => void
  enabled?: boolean
}

interface KeyboardShortcutContextValue {
  registerShortcuts: (id: string, shortcuts: ShortcutAction[]) => void
  unregisterShortcuts: (id: string) => void
  showHelp: boolean
  setShowHelp: (v: boolean) => void
}

// --- Context ---

const KeyboardShortcutContext = createContext<KeyboardShortcutContextValue | null>(null)

export function useKeyboardShortcutContext() {
  const ctx = useContext(KeyboardShortcutContext)
  if (!ctx) throw new Error("useKeyboardShortcutContext must be used within KeyboardShortcutProvider")
  return ctx
}

// Convenience hook: registers shortcuts on mount, unregisters on unmount
export function useKeyboardShortcuts(id: string, shortcuts: ShortcutAction[]) {
  const { registerShortcuts, unregisterShortcuts } = useKeyboardShortcutContext()
  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  useEffect(() => {
    registerShortcuts(id, shortcutsRef.current)
    return () => unregisterShortcuts(id)
  }, [id, registerShortcuts, unregisterShortcuts])

  // Re-register when shortcuts change (handlers may close over new state)
  useEffect(() => {
    registerShortcuts(id, shortcuts)
  }, [id, shortcuts, registerShortcuts])
}

// --- Overlay detection ---

const INTERACTIVE_SELECTOR = 'a[href], button, [role="button"], [role="checkbox"], [role="menuitem"], [role="option"], [role="tab"]'

// Radix renders open dialogs, alert dialogs, popovers (role=dialog), menus and
// select/command lists with these roles, and unmounts them when closed. A
// surface that should keep page shortcuts live (the task side sheet) opts out
// with `data-shortcuts-allowed`.
const OVERLAY_SELECTOR = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]'

function blockingOverlayOpen(): boolean {
  return Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some(
    (el) => !el.closest("[data-shortcuts-allowed]")
  )
}

// --- Provider ---

export function KeyboardShortcutProvider({ children }: { children: ReactNode }) {
  const [showHelp, setShowHelp] = useState(false)
  const registryRef = useRef<Map<string, ShortcutAction[]>>(new Map())

  const registerShortcuts = useCallback((id: string, shortcuts: ShortcutAction[]) => {
    registryRef.current.set(id, shortcuts)
  }, [])

  const unregisterShortcuts = useCallback((id: string) => {
    registryRef.current.delete(id)
  }, [])

  // Collect all shortcuts for the help modal
  const allShortcuts = Array.from(registryRef.current.values()).flat()

  useEffect(() => {
    // Whether the focused control got focus from a click rather than the
    // keyboard. Space presses a keyboard-focused button, but a pill someone
    // just clicked keeps focus, and Space there should reach the shortcuts.
    // (:focus-visible can't tell: Chromium sets it on any key press.)
    let focusFromPointer = false
    const handlePointerDown = () => { focusFromPointer = true }
    const handleFocusKey = (e: KeyboardEvent) => {
      if (e.key === "Tab") focusFromPointer = false
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return

      // Skip if a modifier is held (except Shift for ? key)
      if (e.metaKey || e.ctrlKey || e.altKey) return

      // A key something already handled is spent. Radix closes a dialog on
      // Escape in the capture phase and marks it handled; React unmounts the
      // dialog before the event bubbles here, so the overlay check below would
      // find nothing and the same Escape would also leave the task page.
      if (e.defaultPrevented) return

      // A dialog, menu or picker owns the keyboard while it is open.
      if (blockingOverlayOpen()) return

      const key = e.key

      // Enter on a focused button or link must press it, not run a shortcut;
      // so must Space, when the control was reached by keyboard.
      if (key === "Enter" && target?.closest(INTERACTIVE_SELECTOR)) return
      if (key === " " && target?.closest(INTERACTIVE_SELECTOR) && !focusFromPointer) return

      // Find matching shortcut from all registries
      for (const shortcuts of registryRef.current.values()) {
        for (const shortcut of shortcuts) {
          if (shortcut.key === key && (shortcut.enabled ?? true)) {
            e.preventDefault()
            shortcut.handler()
            return
          }
        }
      }

      // Global fallback: ? shows every registered shortcut
      if (key === "?") {
        e.preventDefault()
        setShowHelp(true)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown, true)
    document.addEventListener("keydown", handleFocusKey, true)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true)
      document.removeEventListener("keydown", handleFocusKey, true)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  return (
    <KeyboardShortcutContext.Provider value={{ registerShortcuts, unregisterShortcuts, showHelp, setShowHelp }}>
      {children}
      <ShortcutHelpDialog
        open={showHelp}
        onOpenChange={setShowHelp}
        shortcuts={allShortcuts}
      />
    </KeyboardShortcutContext.Provider>
  )
}

// --- Help Dialog ---

const isMac = typeof navigator !== "undefined" && navigator.platform?.includes("Mac")

function ShortcutHelpDialog({
  open,
  onOpenChange,
  shortcuts,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  shortcuts: ShortcutAction[]
}) {
  // Group by category
  const grouped = shortcuts.reduce<Record<string, ShortcutAction[]>>((acc, s) => {
    if (!acc[s.category]) acc[s.category] = []
    acc[s.category].push(s)
    return acc
  }, {})

  // Add the built-in ? shortcut
  if (!grouped["Global"]) grouped["Global"] = []
  const hasHelp = grouped["Global"].some(s => s.key === "?")
  if (!hasHelp) {
    grouped["Global"].unshift({ key: "?", description: "Show keyboard shortcuts", category: "Global", handler: () => {} })
  }

  // Add built-in system shortcuts
  const systemShortcuts = [
    { key: `${isMac ? "⌘" : "Ctrl+"}K`, description: "Search everything", category: "System", handler: () => {} },
    { key: `${isMac ? "⌘" : "Ctrl+"}B`, description: "Toggle sidebar", category: "System", handler: () => {} },
  ]
  grouped["System"] = systemShortcuts

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {Object.entries(grouped).map(([category, shortcuts]) => (
            <div key={category}>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {category}
              </h4>
              <div className="space-y-1">
                {shortcuts.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-sm">{s.description}</span>
                    <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-mono font-medium bg-muted border rounded">
                      {s.key.toUpperCase()}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
